import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Ruler, Layers, Calculator, Wrench, Shield, Zap } from "lucide-react";
import { MaskedLines, FadeUp, Eyebrow, EASE, SectionHead } from "@/components/Reveal";
import ApplicationGrid from "@/components/ApplicationGrid";
import DemoForm from "@/components/DemoForm";
import ProductSchema from "@/components/ProductSchema";

const HERO_IMG =
  "https://images.unsplash.com/photo-1622082679766-c5912d9416eb?crop=entropy&cs=srgb&fm=jpg&q=85";

const CHIPS = ["±30 mm accuracy", "Dual GNSS", "IP69K IMU", "<2 h install"];

const FEATURES = [
  { icon: Ruler, title: "Depth & slope guidance", body: "Real-time cut/fill readout against a reference elevation, with a green-zone tolerance you set (default ±30 mm)." },
  { icon: Layers, title: "Field-defined workflows", body: "Work straight from a benchmark — no 3D design files, no office software, no waiting on surveyors." },
  { icon: Calculator, title: "Live cut/fill volumes", body: "The display tracks volumes as you dig, so you always know how much material is left to move." },
  { icon: Wrench, title: "User-defined buckets", body: "Store multiple buckets and calibrate each one in minutes. Swap attachments without a site visit." },
  { icon: Shield, title: "Built for the pit", body: "IP69K IMU, IP68 GNSS antennas, IP65 display. Rated from −30°C to +70°C, on 7–36 V DC machine power." },
  { icon: Zap, title: "Two-hour retrofit", body: "CAN and RS232 integration fits swing boom, triple boom, tilt bucket and offset boom excavators." },
];

const STEPS = [
  { n: "01", title: "Mount", body: "Antennas, IMU and display installed on your machine in under two hours." },
  { n: "02", title: "Calibrate", body: "About thirty minutes per bucket with the guided calibration workflow." },
  { n: "03", title: "Dig", body: "Operators dig to a ±30 mm green zone from their first shift." },
];

const SPECS = [
  { k: "System accuracy", v: "±30 mm (manufacturer rating)" },
  { k: "Positioning", v: "Dual GNSS — GPS, GLONASS, BeiDou, Galileo, QZSS, L-Band · IP68 antennas" },
  { k: "IMU sensor", v: "±0.05° static / ±0.1° dynamic · IP69K · 7–36 V DC · −40°C to +75°C" },
  { k: "Display", v: '10.1" · 1024×600 · 750 cd/m² · Android · IP65 · 1.5 kg' },
  { k: "Connectivity", v: "Wi-Fi · Bluetooth · 4G LTE · external radio support · CAN · RS232" },
  { k: "Environment", v: "−30°C to +70°C operating temperature" },
  { k: "Install & calibration", v: "<2 h mounting · ~30 min calibration · user-defined bucket management" },
];

const PRODUCT_SCHEMA = {
  "@type": "Product",
  name: "CHCNAV EasyNAV EMG100 — 2.5D Excavator Machine Control",
  image: HERO_IMG,
  description:
    "2.5D excavator machine control with ±30 mm accuracy, dual GNSS positioning, IP69K IMU, 30-minute calibration and under 2-hour install. Supplied, installed and supported across Australia by Green Grade.",
  sku: "EasyNAV-EMG100",
  model: "EasyNAV EMG100",
  category: "Construction machine control",
  brand: { "@type": "Brand", name: "CHCNAV" },
  manufacturer: { "@type": "Organization", name: "CHCNAV" },
  url: "https://greengrade.au/products/2.5d",
};

export default function Product25D() {
  return (
    <main>
      <ProductSchema product={PRODUCT_SCHEMA} />
      <section className="relative flex min-h-[88svh] items-end overflow-hidden" data-testid="product-25d-hero">
        <motion.div
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.6, ease: EASE }}
          className="absolute inset-0"
        >
          <img src={HERO_IMG} alt="Excavator cutting grade on brown soil" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-ink/75" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-ink/70" />
        </motion.div>
        <div className="absolute inset-0 bg-blueprint" />
        <div className="relative mx-auto w-full max-w-[1600px] px-4 pb-20 pt-32 sm:px-8 lg:px-16">
          <FadeUp y={12}>
            <Eyebrow>
              <span>
                <Link to="/precision-solutions" data-testid="p25d-eyebrow-chcnav-link" className="underline decoration-neon/40 underline-offset-4 transition-colors hover:text-white">
                  CHCNAV
                </Link>{" "}
                EasyNAV EMG100
              </span>
            </Eyebrow>
          </FadeUp>
          <MaskedLines
            as="h1"
            delay={0.15}
            lines={[
              "2.5D machine",
              { text: "control.", lineClass: "italic text-neon" },
            ]}
            className="mt-4 font-display text-6xl font-black uppercase leading-[0.9] tracking-tight text-white sm:text-8xl lg:text-9xl"
          />
          <p className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
            Dual-GNSS depth and slope guidance for excavators. No design files, no surveyor, no drama — calibrated in
            thirty minutes and digging to ±30 mm from day one.
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            {CHIPS.map((c) => (
              <span key={c} className="border border-signal/40 bg-ink/60 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.2em] text-neon backdrop-blur-sm">
                {c}
              </span>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="product-25d-demo-cta"
              className="group inline-flex items-center gap-3 bg-brand px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-cream transition-colors hover:bg-signal hover:text-deep"
            >
              Request a demo
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <Link
              to="/#quote"
              data-testid="product-25d-quote-cta"
              className="inline-flex items-center gap-3 border border-signal/50 px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-neon transition-colors hover:bg-signal/10"
            >
              Build a quote
            </Link>
            <Link
              to="/products/3d"
              data-testid="product-25d-to-3d-link"
              className="inline-flex items-center gap-3 border border-line px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-white transition-colors hover:border-signal hover:text-neon"
            >
              Or step up to 3D
            </Link>
          </div>
        </div>
      </section>

      <section className="px-4 py-20 sm:px-8 lg:px-16 lg:py-32" data-testid="product-25d-features">
        <div className="mx-auto max-w-[1600px]">
          <SectionHead kicker="What it does" title="Guidance without the homework" body="Everything an operator needs to hit depth and slope — and nothing that needs a design office to run." />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f, i) => (
              <FadeUp key={f.title} delay={i * 0.06}>
                <div className="h-full border border-line bg-surface p-6 transition-colors duration-300 hover:border-signal/50 sm:p-8">
                  <f.icon className="h-6 w-6 text-signal" />
                  <h3 className="mt-5 font-display text-xl font-bold uppercase tracking-tight text-white">{f.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-fog">{f.body}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-line bg-deep px-4 py-20 sm:px-8 lg:px-16 lg:py-28" data-testid="product-25d-steps">
        <div className="mx-auto max-w-[1600px]">
          <SectionHead kicker="Time to first cut" title="Up and digging today" />
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {STEPS.map((s, i) => (
              <FadeUp key={s.n} delay={i * 0.1}>
                <div className="flex h-full gap-6 border border-line bg-surface p-8">
                  <span className="text-stroke font-display text-6xl font-black leading-none">{s.n}</span>
                  <div>
                    <h3 className="font-display text-2xl font-bold uppercase text-white">{s.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-fog">{s.body}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-line bg-deep px-4 py-20 sm:px-8 lg:px-16 lg:py-28" data-testid="p25d-applications">
        <div className="mx-auto max-w-[1600px]">
          <SectionHead
            kicker="Wide applications"
            title="What the EMG100 is perfect for."
            body="No design files, no office software — set a benchmark and hold grade across the whole spread, from paddocks to pipelines."
          />
          <div className="mt-14">
            <ApplicationGrid />
          </div>
        </div>
      </section>

      <section className="border-t border-line bg-deep px-4 py-20 sm:px-8 lg:px-16 lg:py-32" data-testid="product-25d-specs">
        <div className="mx-auto max-w-[1600px]">
          <SectionHead kicker="Technical" title="Specifications" />
          <div className="mt-12">
            {SPECS.map((s, i) => (
              <FadeUp key={s.k} delay={i * 0.04} y={16}>
                <div className="flex flex-col gap-1 border-b border-line py-5 sm:flex-row sm:items-baseline sm:justify-between">
                  <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-fog">{s.k}</span>
                  <span className="max-w-[60%] text-sm text-white sm:text-right sm:text-base">{s.v}</span>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.1} className="mt-10">
            <Link
              to="/specifications#emg100"
              data-testid="product-25d-spec-link"
              className="inline-flex items-center gap-2 border border-line px-6 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-fog transition-colors hover:border-signal hover:text-neon"
            >
              Full specification breakdown <ArrowUpRight className="h-4 w-4" />
            </Link>
          </FadeUp>
        </div>
      </section>

      <section className="px-4 py-20 sm:px-8 lg:px-16 lg:py-32" data-testid="product-25d-form">
        <div className="mx-auto max-w-[1600px]">
          <SectionHead kicker="Get it on your machine" title="Request an EMG100 demo" body="Tell us about your fleet and we'll bring the system to your site — installed and calibrated." />
          <FadeUp delay={0.12} className="mt-12">
            <DemoForm />
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
